import {
  getUnlinkedPayoutProviderNames,
  UNLINKED_PAYOUT_PROVIDERS_FIELD,
} from "pages/CreateOrder/utils/payoutProviderSetup";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetAccountByIdQuery } from "store/queries/account";
import { ProcedureRow } from "types/CreateOrder";

/**
 * CLE-3074 — whether the estimate's account (the form's `accountId`, which a SuperAdmin can set to any
 * hospital) takes payments on Finix. A brand-new estimate goes to the account's rail, so the flag is
 * the right test here. RTK Query shares the account request the page already makes.
 */
export function useIsFinixAccount(): boolean {
  const { control } = useFormContext();
  const accountId = useWatch({ control, name: "accountId" }) as
    | string
    | null
    | undefined;
  const { data } = useGetAccountByIdQuery(accountId || "", {
    skip: !accountId,
  });
  return data?.data?.features?.useFinix === true;
}

/**
 * CLE-3074 — writes the names of the selected providers whose payment setup is incomplete into the
 * form (UNLINKED_PAYOUT_PROVIDERS_FIELD), where useOrderReadiness and the footer read it. Cleared when
 * the service-details section unmounts (e.g. the estimate type changes to Patient Balance).
 */
export function usePublishUnlinkedPayoutProviders(
  rows: ProcedureRow[],
  isFinixAccount: boolean
): void {
  const { getValues, setValue } = useFormContext();

  // Written only when the list changes: every setRows would otherwise hand useOrderReadiness's
  // subscribers a fresh (usually empty) array.
  const publish = (names: string[]) => {
    const current = getValues(UNLINKED_PAYOUT_PROVIDERS_FIELD);
    const unchanged =
      Array.isArray(current) &&
      current.length === names.length &&
      current.every((name, index) => name === names[index]);
    if (!unchanged) setValue(UNLINKED_PAYOUT_PROVIDERS_FIELD, names);
  };

  useEffect(() => {
    publish(getUnlinkedPayoutProviderNames(rows, isFinixAccount));
  }, [rows, isFinixAccount]);

  useEffect(() => () => publish([]), []);
}
